/**
 * Extracted from orca/docker/test/portal/playback.js (media path).
 * Depends on ./multipart.js (orca) and G711 via ./codec.js (orca).
 */
(function (root, factory) {
  const api = factory(
    typeof require !== "undefined" ? require("./multipart") : root.Multipart
  );
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  } else {
    root.PlaybackMedia = api;
  }
}(typeof globalThis !== "undefined" ? globalThis : this, (Multipart) => {
  if (!Multipart) throw new Error("multipart.js must be loaded before playbackMedia.js");
  const { MultipartParser, parseMultipartBoundary } = Multipart;

const PLAYBACK_VIDEO_CODECS = {
  "video/h264": "H264",
  "video/h265": "H265",
  "video/hevc": "H265",
};
const HEVC_MIME = 'video/mp4; codecs="hvc1.1.6.L93.B0"';
const PLAYBACK_AUDIO_CODECS = {
  "audio/pcma": "pcma",
  "audio/pcmu": "pcmu",
  "audio/g711-alaw": "pcma",
  "audio/g711-ulaw": "pcmu",
};
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_PLAYBACK_FRAME_DURATION_MS = 1000;
const PLAYBACK_AUDIO_SAMPLE_RATE = 8000;
const PLAYBACK_AUDIO_BUFFER_SECONDS = 8;
const PLAYBACK_DEFAULT_FPS = 30;
const PLAYBACK_LOW_WATER_SECONDS = 1;
const PLAYBACK_HIGH_WATER_SECONDS = 2;
// Without an audio FIFO to stay in step with, video can hold a deeper cushion.
// The audio worklet ring is only 3s, so the shared marks have to stay small.
const PLAYBACK_VIDEO_LOW_WATER_SECONDS = 1.5;
const PLAYBACK_VIDEO_HIGH_WATER_SECONDS = 5;
const MAX_PLAYBACK_START_DELAY_SECONDS = PLAYBACK_HIGH_WATER_SECONDS - PLAYBACK_LOW_WATER_SECONDS;
const MAX_PLAYBACK_STARTUP_COUNTERPART_PARTS = 1;
const PLAYBACK_BUFFER_POLL_MS = 100;
// Video must not stay paused for an audio gap longer than this.
const MAX_AUDIO_IDLE_PAUSE_MS = 1000;
// Hard cap on buffered lead, so feeding a starved track cannot grow the other
// track's buffer without bound.
const PLAYBACK_MAX_LEAD_SECONDS = 12;
// Rate easing window: some NVR sub-devices deliver below real time, so the
// cushion drains no matter how deep it is. Playing 10-20% slow is far less
// visible than the freeze/resume flicker of riding the buffer edge.
const PLAYBACK_RATE_CRITICAL_SECONDS = 0.5;
const PLAYBACK_RATE_RECOVER_SECONDS = 2;
const MIN_PLAYBACK_RATE = 0.5;
// Play slightly under the measured arrival rate so the cushion can rebuild.
const ARRIVAL_RATE_MARGIN = 0.97;
const ARRIVAL_RATE_SAMPLE_MS = 1000;
const ARRIVAL_RATE_SMOOTHING = 0.3;
// Frame timestamp samples are only needed back to the current playhead.
const MAX_VIDEO_CLOCK_HISTORY_SECONDS = 30;
const PLAYBACK_BUFFER_EVENTS = ["timeupdate", "progress", "playing", "waiting", "emptied"];

const PLAYBACK_DEBUG = false;
const dbg = (...args) => {
  if (PLAYBACK_DEBUG) console.log("[PlaybackDebug]", ...args);
};

function createPlaybackMuxerOptions(videoCodec = "H264") {
  return {
    node: "player",
    mode: "video",
    flushingTime: 0,
    fps: PLAYBACK_DEFAULT_FPS,
    maxDelay: Number.MAX_SAFE_INTEGER,
    debug: false,
    videoCodec,
  };
}

function hevcSupported(mediaSource = globalThis.MediaSource) {
  return typeof mediaSource?.isTypeSupported === "function" && mediaSource.isTypeSupported(HEVC_MIME);
}

class PlaybackVideoMuxer {
  constructor(createMuxer, mediaSource = globalThis.MediaSource) {
    this.createMuxer = createMuxer;
    this.mediaSource = mediaSource;
    this.videoCodec = null;
    this.muxer = null;
    this.ready = false;
    this.readyWaiters = new Set();
    this.generation = 0;
  }

  setVideoCodec(videoCodec) {
    if (this.videoCodec === videoCodec) return;
    if (this.videoCodec !== null) throw new Error("playback video codec changed");
    if (videoCodec === "H265" && !hevcSupported(this.mediaSource)) {
      throw new Error("this browser cannot decode H.265");
    }
    const generation = ++this.generation;
    const options = createPlaybackMuxerOptions(videoCodec);
    options.onReady = () => {
      if (this.generation === generation) this.markReady();
    };
    try {
      this.muxer = this.createMuxer(options);
      this.videoCodec = videoCodec;
    } catch (error) {
      this.generation += 1;
      this.ready = false;
      throw error;
    }
  }

  markReady() {
    if (this.ready) return;
    this.ready = true;
    for (const waiter of this.readyWaiters) waiter.resolve();
    this.readyWaiters.clear();
  }

  waitUntilReady() {
    if (this.ready) return Promise.resolve();
    return new Promise((resolve, reject) => {
      this.readyWaiters.add({ resolve, reject });
    });
  }

  async feed(value) {
    if (!this.muxer) throw new Error("playback video codec unavailable");
    const muxer = this.muxer;
    await this.waitUntilReady();
    if (this.muxer !== muxer) {
      const error = new Error("playback stopped");
      error.name = "AbortError";
      throw error;
    }
    muxer.feed(value);
  }

  destroy() {
    this.generation += 1;
    const error = new Error("playback stopped");
    error.name = "AbortError";
    for (const waiter of this.readyWaiters) waiter.reject(error);
    this.readyWaiters.clear();
    this.muxer?.destroy?.();
    this.muxer = null;
    this.videoCodec = null;
    this.ready = false;
  }
}

function parseDgfFrameTime(value) {
  const match = /^(\d{2})\.(\d{2})\.(\d{2})\.(\d{3})$/.exec(value || "");
  if (!match) return null;
  const [hours, minutes, seconds, milliseconds] = match.slice(1).map(Number);
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return (((hours * 60 + minutes) * 60 + seconds) * 1000) + milliseconds;
}

class PlaybackFrameClock {
  constructor() {
    this.previous = null;
    this.rejectedDeltas = 0;
    this.missingTimestamps = 0;
  }

  duration(value) {
    const current = parseDgfFrameTime(value);
    if (current === null) {
      this.missingTimestamps += 1;
      this.previous = null;
      return null;
    }
    const previous = this.previous;
    this.previous = current;
    if (previous === null) return null;
    const delta = current >= previous
      ? current - previous
      : current + MILLISECONDS_PER_DAY - previous;
    if (delta > 0 && delta <= MAX_PLAYBACK_FRAME_DURATION_MS) return delta;
    this.rejectedDeltas += 1;
    return null;
  }
}

function dgfTimeOfDayMs(epochMs) {
  return ((epochMs % MILLISECONDS_PER_DAY) + MILLISECONDS_PER_DAY) % MILLISECONDS_PER_DAY;
}

function signedDgfTimestampDelta(later, earlier) {
  let delta = later - earlier;
  if (delta > MILLISECONDS_PER_DAY / 2) delta -= MILLISECONDS_PER_DAY;
  else if (delta < -MILLISECONDS_PER_DAY / 2) delta += MILLISECONDS_PER_DAY;
  return delta;
}

class PlaybackTimeline {
  constructor() {
    this.firstVideoTimestamp = null;
    this.firstAudioTimestamp = null;
    this.counterpartProbePartsRemaining = 0;
  }

  observe(part) {
    const isVideo = Boolean(PLAYBACK_VIDEO_CODECS[part.contentType]);
    const isAudio = Boolean(PLAYBACK_AUDIO_CODECS[part.contentType]);
    const wasWaitingForCounterpart = this.requiresCounterpartTimestamp();
    const timestamp = parseDgfFrameTime(part.headers?.["dgf-frametime"]);
    if (timestamp !== null && isVideo && this.firstVideoTimestamp === null) {
      this.firstVideoTimestamp = timestamp;
    }
    if (timestamp !== null && isAudio && this.firstAudioTimestamp === null) {
      this.firstAudioTimestamp = timestamp;
    }
    if (!this.requiresCounterpartTimestamp()) {
      this.counterpartProbePartsRemaining = 0;
    } else if (!wasWaitingForCounterpart) {
      this.counterpartProbePartsRemaining = MAX_PLAYBACK_STARTUP_COUNTERPART_PARTS;
    } else {
      this.counterpartProbePartsRemaining = Math.max(0, this.counterpartProbePartsRemaining - 1);
    }
  }

  requiresCounterpartTimestamp() {
    return (this.firstVideoTimestamp === null) !== (this.firstAudioTimestamp === null);
  }

  canProbeCounterpart() {
    return this.requiresCounterpartTimestamp() && this.counterpartProbePartsRemaining > 0;
  }

  videoStartDelaySeconds() {
    if (this.firstVideoTimestamp === null || this.firstAudioTimestamp === null) return 0;
    const offsetMs = signedDgfTimestampDelta(this.firstVideoTimestamp, this.firstAudioTimestamp);
    return Math.min(
      MAX_PLAYBACK_START_DELAY_SECONDS,
      Math.max(0, offsetMs / 1000),
    );
  }
}

/**
 * Media-clock mapping: playhead = firstFrameActualPts + presented mediaTime.
 * The first decoded frame's absolute dgf PTS is the timeline base (not the
 * user's seek target). After that only mediaTime advances the playhead —
 * no wall-clock / setInterval accumulation. seekOffsetMs is diagnostic only.
 */
class PlaybackVideoClock {
  constructor() {
    this.samples = [];
    this.mediaSeconds = 0;
    this.anchorEpochMs = null;
    this.previousFrameTimeMs = null;
    this.elapsedMs = 0;
    this.seekOffsetMs = null;
    // Absolute unix ms of the first observed frame's dgf-frametime (Latch base).
    this.firstFramePtsEpochMs = null;
    this.mediaOffsetSeconds = null;
  }

  /** Unix ms of the requested seek position, i.e. media time zero. */
  setAnchorEpochMs(epochMs) {
    this.anchorEpochMs = Number.isFinite(epochMs) ? epochMs : null;
    this.seekOffsetMs = null;
    this.firstFramePtsEpochMs = null;
  }

  /**
   * Align our feed cursor with the element timeline (muxer may drop pre-SPS
   * frames). Diagnostic / buffer math only — not used for the UI playhead.
   */
  calibrate(bufferedEndSeconds) {
    if (!Number.isFinite(bufferedEndSeconds) || bufferedEndSeconds <= 0) return;
    const behind = this.mediaSeconds - bufferedEndSeconds;
    if (behind < 0) return;
    if (this.mediaOffsetSeconds === null || behind < this.mediaOffsetSeconds) {
      this.mediaOffsetSeconds = behind;
    }
  }

  observe(frameTimeValue, durationSeconds) {
    const frameTimeMs = parseDgfFrameTime(frameTimeValue);
    if (frameTimeMs !== null && this.anchorEpochMs !== null) {
      if (this.previousFrameTimeMs === null) {
        // Map dgf time-of-day onto the seek's date (whole minutes stripped so
        // recorder timezone cancels). This absolute PTS is the Latch base.
        const delta = signedDgfTimestampDelta(frameTimeMs, dgfTimeOfDayMs(this.anchorEpochMs));
        this.seekOffsetMs = delta - (Math.round(delta / 60000) * 60000);
        this.firstFramePtsEpochMs = this.anchorEpochMs + this.seekOffsetMs;
        this.elapsedMs = 0;
      } else {
        this.elapsedMs += signedDgfTimestampDelta(frameTimeMs, this.previousFrameTimeMs);
      }
      this.previousFrameTimeMs = frameTimeMs;
      this.samples.push({ mediaSeconds: this.mediaSeconds, elapsedMs: this.elapsedMs });
      this.prune();
    }
    if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
      this.mediaSeconds += durationSeconds;
    }
  }

  prune() {
    const keepFrom = this.mediaSeconds - MAX_VIDEO_CLOCK_HISTORY_SECONDS;
    let drop = 0;
    while (drop + 1 < this.samples.length && this.samples[drop + 1].mediaSeconds < keepFrom) {
      drop += 1;
    }
    if (drop > 0) this.samples.splice(0, drop);
  }

  /** Recording elapsed from first observed frame (diagnostic). */
  elapsedMsAt(mediaSeconds) {
    if (!this.samples.length || !Number.isFinite(mediaSeconds)) return null;
    const cursor = mediaSeconds + (this.mediaOffsetSeconds ?? 0);
    let low = 0;
    let high = this.samples.length - 1;
    let index = 0;
    while (low <= high) {
      const mid = (low + high) >> 1;
      if (this.samples[mid].mediaSeconds <= cursor) {
        index = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return this.samples[index].elapsedMs;
  }

  /**
   * Playhead: firstFrameActualPtsMs + mediaTime×1000.
   * Null until the first frame header has been observed.
   */
  epochMsAt(mediaSeconds) {
    if (this.firstFramePtsEpochMs === null || !Number.isFinite(mediaSeconds)) return null;
    return this.firstFramePtsEpochMs + (Math.max(0, mediaSeconds) * 1000);
  }

  /** Alias used by the RVFC playhead path. */
  ptsEpochMsAt(mediaSeconds) {
    return this.epochMsAt(mediaSeconds);
  }
}

function bufferedLead(media) {
  const ranges = media?.buffered;
  if (!ranges?.length) return 0;
  const start = ranges.start(0);
  const end = ranges.end(ranges.length - 1);
  const currentTime = Number.isFinite(media.currentTime) ? media.currentTime : start;
  return Math.max(0, end - Math.max(currentTime, start));
}

/**
 * Rate that keeps a small cushion instead of stalling at the buffer edge.
 * `slowRate` is what to play at when the cushion is gone — for a source that
 * cannot keep up, that is the rate it actually delivers at.
 */
function playbackRateForLead(leadSeconds, slowRate = 0.9) {
  const slow = Math.min(1, Math.max(MIN_PLAYBACK_RATE, slowRate));
  if (!Number.isFinite(leadSeconds) || leadSeconds >= PLAYBACK_RATE_RECOVER_SECONDS) return 1;
  if (leadSeconds <= PLAYBACK_RATE_CRITICAL_SECONDS) return slow;
  const span = PLAYBACK_RATE_RECOVER_SECONDS - PLAYBACK_RATE_CRITICAL_SECONDS;
  const ratio = (leadSeconds - PLAYBACK_RATE_CRITICAL_SECONDS) / span;
  return slow + (ratio * (1 - slow));
}

function applyPlaybackRate(media, rate) {
  if (!media || !Number.isFinite(rate)) return;
  // Ignore small corrections: every assignment resamples audio and can hitch.
  if (Math.abs(media.playbackRate - rate) < 0.02) return;
  media.playbackRate = rate;
}

function playbackAbortError() {
  if (typeof DOMException === "function") return new DOMException("Playback stopped", "AbortError");
  const error = new Error("Playback stopped");
  error.name = "AbortError";
  return error;
}

function playbackNow() {
  return typeof globalThis.performance?.now === "function"
    ? globalThis.performance.now()
    : Date.now();
}

function waitForPlaybackDelay(seconds, signal) {
  if (!seconds) return Promise.resolve();
  if (signal?.aborted) return Promise.reject(playbackAbortError());
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, seconds * 1000);
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      reject(playbackAbortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    if (signal?.aborted) onAbort();
  });
}

class PlaybackAudioBuffer {
  constructor(audioSink, { now = playbackNow, startDelaySeconds = PLAYBACK_LOW_WATER_SECONDS } = {}) {
    this.audioSink = audioSink;
    this.now = now;
    this.startDelaySeconds = startDelaySeconds;
    this.queuedSeconds = 0;
    this.lastNow = null;
    this.active = false;
  }

  canFeed() {
    const state = this.audioSink?.context?.state;
    if (state !== undefined && state !== "running") return false;
    if (this.audioSink && "renderer" in this.audioSink && !this.audioSink.renderer) return false;
    return !this.audioSink?.rendererError;
  }

  waitUntilCanFeed(signal) {
    if (signal?.aborted) return Promise.reject(playbackAbortError());
    if (this.canFeed()) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const context = this.audioSink?.context;
      let settled = false;
      const cleanup = () => {
        context?.removeEventListener?.("statechange", check);
        signal?.removeEventListener("abort", onAbort);
        clearInterval(timer);
      };
      const settle = (callback, value) => {
        if (settled) return;
        settled = true;
        cleanup();
        callback(value);
      };
      const check = () => {
        if (this.canFeed()) settle(resolve);
      };
      const onAbort = () => settle(reject, playbackAbortError());
      const timer = setInterval(check, PLAYBACK_BUFFER_POLL_MS);

      context?.addEventListener?.("statechange", check);
      signal?.addEventListener("abort", onAbort, { once: true });
      if (signal?.aborted) onAbort();
      else check();
    });
  }

  canConsume() {
    const state = this.audioSink?.context?.state;
    if (state !== undefined && state !== "running") return false;
    if (this.audioSink && "renderer" in this.audioSink && !this.audioSink.renderer) return false;
    if (this.audioSink && "playbackStarted" in this.audioSink && !this.audioSink.playbackStarted) return false;
    return this.queuedSeconds >= this.startDelaySeconds;
  }

  resetIfSinkStopped() {
    if (this.active && this.audioSink && "playbackStarted" in this.audioSink && !this.audioSink.playbackStarted) {
      this.reset();
    }
  }

  update() {
    const current = Number(this.now());
    if (!Number.isFinite(current)) return;
    if (this.lastNow === null) {
      this.lastNow = current;
      return;
    }
    const elapsed = Math.max(0, (current - this.lastNow) / 1000);
    this.lastNow = current;
    this.resetIfSinkStopped();
    if (!elapsed || !this.canConsume()) return;
    const playbackRate = Number.isFinite(this.audioSink?.playbackRate) && this.audioSink.playbackRate > 0
      ? this.audioSink.playbackRate
      : 1;
    this.queuedSeconds = Math.max(0, this.queuedSeconds - elapsed * playbackRate);
  }

  leadSeconds() {
    if (typeof this.audioSink?.playbackPosition === "function") {
      return this.audioSink.playbackPosition().queuedSeconds;
    }
    this.update();
    return this.queuedSeconds;
  }

  append(durationSeconds) {
    this.update();
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return;
    this.queuedSeconds += durationSeconds;
    this.active = true;
  }

  reset() {
    this.queuedSeconds = 0;
    this.lastNow = Number(this.now());
    this.active = false;
  }
}

class PlaybackOutputSync {
  constructor(media, audioSink, { getArrivalRate = () => null } = {}) {
    this.media = media;
    this.audioSink = audioSink;
    this.getArrivalRate = getArrivalRate;
    this.lastLoggedMode = null;
    this.audioIdleSince = null;
    this.mediaBaselineSeconds = null;
    this.lastPlayedSeconds = 0;
  }

  /** Floor for rate corrections: follow source delivery, never a fixed 0.9. */
  slowRate() {
    const arrival = this.getArrivalRate?.();
    if (!Number.isFinite(arrival) || arrival <= 0) return 0.9;
    return Math.min(1, Math.max(MIN_PLAYBACK_RATE, arrival * ARRIVAL_RATE_MARGIN));
  }

  /**
   * Media time the audio output clock starts from. Zero at stream start, but
   * audio can also be turned on mid-stream, where its clock restarts from zero
   * while video is already minutes in.
   */
  baselineSeconds(position) {
    if (this.mediaBaselineSeconds === null || position.playedSeconds < this.lastPlayedSeconds) {
      this.mediaBaselineSeconds = Math.max(0, this.media.currentTime - position.playedSeconds);
      dbg("av-sync: anchor audio clock", {
        t: Number(playbackNow().toFixed(1)),
        mediaBaselineSeconds: this.mediaBaselineSeconds,
        currentTime: this.media.currentTime,
        playedSeconds: position.playedSeconds,
      });
    }
    this.lastPlayedSeconds = position.playedSeconds;
    return this.mediaBaselineSeconds;
  }

  update(position, videoOffset, inputEnded = false) {
    if (!this.media) return;
    const baseline = this.baselineSeconds(position);
    if (inputEnded && position.queuedSeconds === 0 && !position.playing) {
      this.audioSink.holdPlayback(false);
      this.media.playbackRate = 1;
      if (this.media.paused) Promise.resolve(this.media.play()).catch(() => {});
      return;
    }
    const target = baseline + position.playedSeconds - videoOffset;
    const error = target - this.media.currentTime;
    const slowRate = this.slowRate();
    // Hold audio when video lags so video can catch up. Never pause video while
    // held — that freezes both sides (see PlaybackDebug av-sync deadlock).
    const hold = error > 0.12;
    this.audioSink.holdPlayback(hold);
    if (hold || position.held) {
      this.audioIdleSince = null;
      const catchUpRate = Math.min(1.1, Math.max(1, 1 + error * 0.5));
      this.media.playbackRate = catchUpRate;
      if (this.media.paused) Promise.resolve(this.media.play()).catch(() => {});
      return;
    }
    // Audio has not reached the first video frame yet (startup AV offset). Wait
    // for it, but only briefly so a missing audio track cannot block startup.
    if (target < 0) {
      const now = Date.now();
      if (this.audioIdleSince === null) this.audioIdleSince = now;
      if (now - this.audioIdleSince > MAX_AUDIO_IDLE_PAUSE_MS) {
        this.media.playbackRate = 1;
        if (this.media.paused) Promise.resolve(this.media.play()).catch(() => {});
        return;
      }
      this.media.playbackRate = 1;
      this.media.pause();
      return;
    }
    // Mid-stream audio underrun: the FIFO rebuffers every few seconds on some
    // NVR sub-devices. Pausing video each time turns that into visible stutter,
    // so keep it rolling at the source arrival rate and let audio catch up.
    if (!position.playing) {
      const now = Date.now();
      if (this.audioIdleSince === null) this.audioIdleSince = now;
      const lead = bufferedLead(this.media);
      applyPlaybackRate(this.media, playbackRateForLead(lead, slowRate));
      if (this.lastLoggedMode !== "audio-underrun") {
        this.lastLoggedMode = "audio-underrun";
        dbg("[Rate & Underrun Event]", {
          t: Number(playbackNow().toFixed(1)),
          sourceRate: Number.isFinite(this.getArrivalRate?.())
            ? Number(this.getArrivalRate().toFixed(2))
            : null,
          computedSlowRate: Number(slowRate.toFixed(2)),
          currentTime: Number(this.media.currentTime?.toFixed(2)),
          videoLead: Number(lead.toFixed(2)),
          queuedSeconds: Number(position.queuedSeconds?.toFixed(2)),
          playbackRate: Number(this.media.playbackRate?.toFixed(2)),
        });
      }
      if (this.media.paused) Promise.resolve(this.media.play()).catch(() => {});
      return;
    }
    this.audioIdleSince = null;
    this.lastLoggedMode = "playing";
    // Floor at the arrival rate, not 0.9: an NVR at ~0.7× would otherwise
    // drain the cushion and stall every few seconds.
    const correction = Math.abs(error) < 0.02 ? 1 : 1 + (error * 0.5);
    this.media.playbackRate = Math.min(1.1, Math.max(slowRate, correction));
    if (this.media.paused) Promise.resolve(this.media.play()).catch(() => {});
  }
}

class PlaybackBufferController {
  constructor(media, {
    lowSeconds = PLAYBACK_LOW_WATER_SECONDS,
    highSeconds = PLAYBACK_HIGH_WATER_SECONDS,
    audioBuffer = null,
    startDelaySeconds = 0,
  } = {}) {
    this.media = media;
    this.lowSeconds = lowSeconds;
    this.highSeconds = highSeconds;
    this.audioBuffer = audioBuffer;
    this.startDelaySeconds = 0;
    this.startupSyncReady = true;
    this.startupSyncProbeAvailable = false;
    this.started = false;
    this.videoObserved = false;
    this.audioObserved = false;
    this.submittedVideoSeconds = 0;
    this.refilling = false;
    this.videoOnly = false;
    this.arrivalRate = null;
    this.arrivalSampledAt = null;
    this.arrivalSubmitted = 0;
    this.setStartDelaySeconds(startDelaySeconds);
  }

  /**
   * Without audio the video track owns both the cushion depth and the rate,
   * since nothing else steers the element.
   */
  setVideoOnly(videoOnly) {
    if (this.videoOnly === videoOnly) return;
    this.videoOnly = videoOnly;
    this.lowSeconds = videoOnly ? PLAYBACK_VIDEO_LOW_WATER_SECONDS : PLAYBACK_LOW_WATER_SECONDS;
    this.highSeconds = videoOnly ? PLAYBACK_VIDEO_HIGH_WATER_SECONDS : PLAYBACK_HIGH_WATER_SECONDS;
  }

  appendVideo(durationSeconds) {
    if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
      this.submittedVideoSeconds += durationSeconds;
      this.videoObserved = true;
    }
  }

  bufferState() {
    const currentTime = Number.isFinite(this.media?.currentTime) ? this.media.currentTime : 0;
    const videoLead = bufferedLead(this.media);
    const submittedVideoLead = Math.max(videoLead, this.submittedVideoSeconds - currentTime);
    const audioLead = typeof this.audioBuffer?.leadSeconds === "function"
      ? this.audioBuffer.leadSeconds()
      : 0;
    if (videoLead > 0) this.videoObserved = true;
    if (audioLead > 0 || this.audioBuffer?.active === true) this.audioObserved = true;
    const activeLeads = [];
    if (this.videoObserved) activeLeads.push(videoLead);
    if (this.audioObserved) activeLeads.push(audioLead);
    const highLeads = [];
    if (this.videoObserved) highLeads.push(submittedVideoLead);
    if (this.audioObserved) highLeads.push(audioLead);
    return {
      activeLeads,
      highLead: highLeads.length ? Math.max(...highLeads) : 0,
      lowLead: activeLeads.length ? Math.min(...activeLeads) : 0,
    };
  }

  leadSeconds() {
    return this.bufferState().highLead;
  }

  canRefillTracks(state) {
    if (state.activeLeads.length < 2) return false;
    if (state.lowLead <= this.lowSeconds) this.refilling = true;
    if (this.refilling && state.lowLead >= this.highSeconds) this.refilling = false;
    // A fully starved track must be refilled even when the other track sits past
    // the high-water cap: video paused waiting for audio while reads are blocked
    // by the video lead can never recover on its own.
    if (state.lowLead <= 0) return true;
    return this.refilling && state.highLead < this.highSeconds + (this.lowSeconds * 2);
  }

  /**
   * Backpressure gate. With two tracks the starved one decides: gating on the
   * larger lead lets a dense video track block audio refills until the audio
   * FIFO underruns, which shows up as periodic video stutter.
   */
  shouldRead(state) {
    if (state.highLead >= PLAYBACK_MAX_LEAD_SECONDS) return state.lowLead <= 0;
    const gateLead = state.activeLeads.length > 1 ? state.lowLead : state.highLead;
    return gateLead < this.highSeconds || this.canRefillTracks(state);
  }

  start(signal) {
    if (this.started) return;
    this.started = true;
    if (!this.media) return;
    dbg("[Buffer Start]", {
      t: Number(playbackNow().toFixed(1)),
      videoOnly: this.videoOnly,
      arrivalRate: Number.isFinite(this.arrivalRate) ? Number(this.arrivalRate.toFixed(2)) : null,
      startDelaySeconds: this.startDelaySeconds,
      currentTime: Number(this.media.currentTime?.toFixed(3)),
      videoLead: Number(bufferedLead(this.media).toFixed(2)),
    });
    this.media.defaultPlaybackRate = 1;
    this.media.playbackRate = 1;
    if (!this.startDelaySeconds) {
      if (signal?.aborted) return Promise.reject(playbackAbortError());
      Promise.resolve(this.media.play?.()).catch(() => {});
      return null;
    }
    return waitForPlaybackDelay(this.startDelaySeconds, signal).then(() => {
      if (signal?.aborted) throw playbackAbortError();
      Promise.resolve(this.media?.play?.()).catch(() => {});
    });
  }

  setStartDelaySeconds(seconds) {
    if (this.started) return;
    this.startDelaySeconds = Number.isFinite(seconds)
      ? Math.min(MAX_PLAYBACK_START_DELAY_SECONDS, Math.max(0, seconds))
      : 0;
  }

  setStartupSyncReady(ready) {
    if (this.started) return;
    this.startupSyncReady = ready !== false;
    if (this.startupSyncReady) this.startupSyncProbeAvailable = false;
  }

  setStartupSyncProbeAvailable(available) {
    if (this.started) return;
    this.startupSyncProbeAvailable = !this.startupSyncReady && available === true;
  }

  pauseForAudio() {
    if (!this.started || !this.media || this.media.paused === true) return false;
    this.media.pause?.();
    return true;
  }

  resumeAfterAudio(wasPlaying) {
    if (!wasPlaying || !this.media) return;
    this.media.defaultPlaybackRate = 1;
    this.media.playbackRate = 1;
    Promise.resolve(this.media.play?.()).catch(() => {});
  }

  /** Recorded seconds arriving per wall second, smoothed. */
  sampleArrivalRate() {
    const now = playbackNow();
    if (this.arrivalSampledAt === null) {
      this.arrivalSampledAt = now;
      this.arrivalSubmitted = this.submittedVideoSeconds;
      return;
    }
    const elapsed = (now - this.arrivalSampledAt) / 1000;
    if (elapsed * 1000 < ARRIVAL_RATE_SAMPLE_MS) return;
    const delivered = this.submittedVideoSeconds - this.arrivalSubmitted;
    this.arrivalSampledAt = now;
    this.arrivalSubmitted = this.submittedVideoSeconds;
    const rate = delivered / elapsed;
    this.arrivalRate = this.arrivalRate === null
      ? rate
      : (this.arrivalRate * (1 - ARRIVAL_RATE_SMOOTHING)) + (rate * ARRIVAL_RATE_SMOOTHING);
  }

  /** Trim the rate toward the arrival rate while nothing else steers video. */
  easePlaybackRate() {
    if (!this.media) return;
    // Always sample: AV sync also needs the arrival rate when the source is slow.
    this.sampleArrivalRate();
    if (!this.videoOnly || !this.started) return;
    // A device that delivers below real time can only be played at its rate;
    // anything faster drains the cushion and shows up as repeated stutter.
    const slowRate = (this.arrivalRate ?? 1) * ARRIVAL_RATE_MARGIN;
    applyPlaybackRate(this.media, playbackRateForLead(bufferedLead(this.media), slowRate));
  }

  beforeRead(signal) {
    if (signal?.aborted) return Promise.reject(playbackAbortError());
    this.easePlaybackRate();
    let state = this.bufferState();
    let lead = state.highLead;
    // The low-water mark already represents the audio worklet's initial
    // playout cushion. Apply the recorded AV offset only in start(), rather
    // than counting it once in the buffer threshold and again as a timer.
    const startThreshold = this.startupSyncReady ? this.lowSeconds : this.highSeconds;
    const startLead = state.activeLeads.length > 1 ? state.lowLead : lead;
    if (!this.started && startLead >= startThreshold) {
      if (!this.startupSyncReady && this.startupSyncProbeAvailable) return Promise.resolve();
      if (!this.startupSyncReady) this.startDelaySeconds = 0;
      const starting = this.start(signal);
      if (starting) return starting.then(() => this.beforeRead(signal));
      state = this.bufferState();
      lead = state.highLead;
    }
    if (this.shouldRead(state)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;
      const cleanup = () => {
        if (this.media) {
          for (const eventName of PLAYBACK_BUFFER_EVENTS) {
            this.media.removeEventListener(eventName, check);
          }
        }
        signal?.removeEventListener("abort", onAbort);
        if (timer !== null) clearInterval(timer);
      };
      const settle = (callback, value) => {
        if (settled) return;
        settled = true;
        cleanup();
        callback(value);
      };
      const check = () => {
        this.easePlaybackRate();
        const state = this.bufferState();
        const lead = state.highLead;
        const position = this.audioBuffer?.audioSink?.playbackPosition?.();
        const audioWaiting = position && (position.held || !position.playing);
        const videoWaiting = this.media && (this.media.paused || this.media.readyState < 3);
        // A held audio FIFO and a video waiting for more decoded frames cannot
        // drain to low water. Refill within the existing high-water bound.
        if (this.shouldRead(state)
          || (audioWaiting && videoWaiting && lead < this.highSeconds)) {
          settle(resolve);
        }
      };
      const onAbort = () => settle(reject, playbackAbortError());

      if (this.media) {
        for (const eventName of PLAYBACK_BUFFER_EVENTS) {
          this.media.addEventListener(eventName, check);
        }
      }
      signal?.addEventListener("abort", onAbort, { once: true });
      timer = setInterval(check, PLAYBACK_BUFFER_POLL_MS);
      if (signal?.aborted) onAbort();
      else check();
    });
  }

  finish(signal) {
    if (!this.startupSyncReady) this.startDelaySeconds = 0;
    const starting = this.start(signal);
    if (starting) return starting;
    return Promise.resolve();
  }
}

async function playMultipartResponse(response, {
  muxer,
  audioSink,
  audioBuffer = null,
  media = null,
  videoClock = null,
  signal,
  onType = () => {},
  onAudioBlocked = () => {},
  onAudioReady = () => {},
  // Muted players (boolean or getter) never render audio, so feeding the sink
  // would only make the AV clock gate video for a track nobody hears.
  dropAudio = false,
  // Players without an "enable audio" affordance must not block on a suspended
  // AudioContext; they drop audio parts until the output starts instead.
  dropAudioWhenBlocked = false,
}) {
  const contentType = response.headers.get("content-type") || "";
  let boundary;
  try {
    boundary = parseMultipartBoundary(contentType);
  } catch (_error) {
    throw new Error(`unsupported playback response type: ${contentType || "missing"}`);
  }
  if (!response.ok || !response.body) throw new Error("playback stream unavailable");

  const frameClock = new PlaybackFrameClock();
  const playbackTimeline = new PlaybackTimeline();
  const playbackAudioBuffer = audioBuffer || new PlaybackAudioBuffer(audioSink);
  const pendingParts = [];
  const parser = new MultipartParser({
    boundary,
    onPart: (part) => {
      playbackTimeline.observe(part);
      pendingParts.push(part);
    },
  });
  const bufferController = new PlaybackBufferController(media, { audioBuffer: playbackAudioBuffer });
  let inputEnded = false;
  let videoParts = 0;
  let audioParts = 0;
  let otherParts = 0;
  let droppedAudioParts = 0;
  let fedAudioParts = 0;
  let lastProgress = { at: performance.now(), submitted: 0, currentTime: 0 };
  const audioMuted = () => (typeof dropAudio === "function" ? dropAudio() === true : dropAudio === true);
  // Dropped audio means there is no audio output to sync against, so the video
  // track owns startup buffering on its own.
  const videoOnly = () => audioMuted() || (droppedAudioParts > 0 && fedAudioParts === 0);
  const syncStartupGates = () => {
    const withoutAudio = videoOnly();
    bufferController.setStartupSyncReady(
      withoutAudio || !playbackTimeline.requiresCounterpartTimestamp()
    );
    bufferController.setStartupSyncProbeAvailable(
      !withoutAudio && playbackTimeline.canProbeCounterpart()
    );
    bufferController.setStartDelaySeconds(
      withoutAudio ? 0 : playbackTimeline.videoStartDelaySeconds()
    );
    bufferController.setVideoOnly(withoutAudio);
  };
  if (typeof audioSink?.playbackPosition === "function" && media) {
    const sync = new PlaybackOutputSync(media, audioSink, {
      getArrivalRate: () => bufferController.arrivalRate,
    });
    const previous = audioSink.onRendererMessage;
    const handler = (message) => {
      previous?.(message);
      if (message.type !== "playback-position" || signal?.aborted) return;
      if (fedAudioParts === 0) return;
      if (audioMuted()) {
        // Audio turned off mid-stream: stop steering video with a dead clock.
        if (media.playbackRate !== 1) media.playbackRate = 1;
        return;
      }
      if (playbackTimeline.firstAudioTimestamp === null || playbackTimeline.firstVideoTimestamp === null) return;
      bufferController.sampleArrivalRate();
      bufferController.started = true;
      const offset = signedDgfTimestampDelta(playbackTimeline.firstVideoTimestamp, playbackTimeline.firstAudioTimestamp) / 1000;
      sync.update(audioSink.playbackPosition(), offset, inputEnded);
    };
    audioSink.onRendererMessage = handler;
    signal?.addEventListener("abort", () => {
      if (audioSink.onRendererMessage === handler) audioSink.onRendererMessage = previous;
    }, { once: true });
  }
  /**
   * One line every couple of seconds. `sourceRate` is recorded seconds arriving
   * per wall second: below 1 means the device cannot feed real-time playback,
   * which no amount of buffering can hide. `clockDriftMs` is how far media time
   * has wandered from the recorded frame timestamps.
   */
  const reportProgress = () => {
    if (!PLAYBACK_DEBUG) return;
    const now = performance.now();
    if (now - lastProgress.at < 2000) return;
    const wallSeconds = (now - lastProgress.at) / 1000;
    const submitted = bufferController.submittedVideoSeconds;
    const currentTime = media?.currentTime ?? 0;
    const state = bufferController.bufferState();
    const audioPosition = audioSink?.playbackPosition?.();
    const bufferedRanges = media?.buffered;
    if (bufferedRanges?.length) {
      videoClock?.calibrate(bufferedRanges.end(bufferedRanges.length - 1));
    }
    const recordingMs = videoClock && media ? videoClock.epochMsAt(currentTime) : null;
    dbg("playback", {
      videoParts,
      pending: pendingParts.length,
      sourceRate: Number(((submitted - lastProgress.submitted) / wallSeconds).toFixed(2)),
      playedRate: Number(((currentTime - lastProgress.currentTime) / wallSeconds).toFixed(2)),
      rate: Number((media?.playbackRate ?? 0).toFixed(2)),
      // Recorder clock minus requested seek, for comparing against the OSD.
      seekOffsetMs: videoClock?.seekOffsetMs ?? null,
      // Media time fed but never put on the element timeline (pre-keyframe).
      mediaOffsetMs: videoClock?.mediaOffsetSeconds === null || videoClock === null
        ? null
        : Math.round(videoClock.mediaOffsetSeconds * 1000),
      videoLead: Number(bufferedLead(media).toFixed(2)),
      audioLead: Number(state.lowLead.toFixed(2)),
      readyState: media?.readyState,
      droppedAudioParts,
      frameRejects: frameClock.rejectedDeltas,
      clockDriftMs: videoClock
        ? Math.round((videoClock.elapsedMsAt(currentTime) ?? 0) - (currentTime * 1000))
        : null,
      recordingIso: Number.isFinite(recordingMs) ? new Date(recordingMs).toISOString() : null,
      audio: audioPosition && fedAudioParts > 0
        ? {
            queued: Number(audioPosition.queuedSeconds?.toFixed?.(2)),
            playing: audioPosition.playing,
            held: audioPosition.held,
          }
        : null,
    });
    lastProgress = { at: now, submitted, currentTime };
  };
  const processPart = async (part) => {
    onType(part.contentType);
    const videoCodec = PLAYBACK_VIDEO_CODECS[part.contentType];
    if (videoCodec) {
      videoParts += 1;
      muxer.setVideoCodec?.(videoCodec);
      const duration = frameClock.duration(part.headers["dgf-frametime"]);
      // A chunk fed without a duration is held pending by the muxer and merged
      // into the next one, so it does not advance the element timeline at all.
      // Counting a nominal frame here walks the playhead off the picture.
      const mediaSeconds = duration === null ? 0 : duration / 1000;
      videoClock?.observe(part.headers["dgf-frametime"], mediaSeconds);
      if (videoParts === 1) {
        dbg("[First Frame Arrived]", {
          t: Number(playbackNow().toFixed(1)),
          pts: part.headers["dgf-frametime"] ?? null,
          firstFramePtsMs: videoClock?.firstFramePtsEpochMs ?? null,
          firstFramePtsIso: Number.isFinite(videoClock?.firstFramePtsEpochMs)
            ? new Date(videoClock.firstFramePtsEpochMs).toISOString()
            : null,
          seekOffsetMs: videoClock?.seekOffsetMs ?? null,
          targetSeekIso: Number.isFinite(videoClock?.anchorEpochMs)
            ? new Date(videoClock.anchorEpochMs).toISOString()
            : null,
          durationMs: duration,
        });
      }
      await muxer.feed(duration === null
        ? { video: part.body }
        : { video: part.body, duration });
      bufferController.appendVideo(mediaSeconds);
    }
    const audioCodec = PLAYBACK_AUDIO_CODECS[part.contentType];
    if (audioCodec) {
      audioParts += 1;
      const canFeedAudio = typeof playbackAudioBuffer?.canFeed !== "function"
        || playbackAudioBuffer.canFeed();
      const muted = audioMuted();
      if (muted || (!canFeedAudio && dropAudioWhenBlocked)) {
        droppedAudioParts += 1;
        if (!muted) onAudioBlocked();
        return;
      }
      const wasPlaying = canFeedAudio ? false : bufferController.pauseForAudio();
      if (!canFeedAudio) {
        onAudioBlocked();
        await playbackAudioBuffer.waitUntilCanFeed(signal);
        onAudioReady();
      }
      audioSink.feed(part.body, audioCodec, PLAYBACK_LOW_WATER_SECONDS, 1);
      fedAudioParts += 1;
      if (fedAudioParts === 1) {
        dbg("[First Audio Arrived]", {
          t: Number(playbackNow().toFixed(1)),
          pts: part.headers["dgf-frametime"] ?? null,
          seekOffsetMs: videoClock?.seekOffsetMs ?? null,
          contentType: part.contentType,
        });
      }
      playbackAudioBuffer?.append?.(part.body.length / PLAYBACK_AUDIO_SAMPLE_RATE);
      bufferController.resumeAfterAudio(wasPlaying);
    }
    if (!videoCodec && !audioCodec) otherParts += 1;
    reportProgress();
  };
  const drainParts = async () => {
    while (pendingParts.length) {
      if (typeof audioSink?.playbackPosition === "function" && media && fedAudioParts > 0
        && playbackTimeline.firstAudioTimestamp !== null && playbackTimeline.firstVideoTimestamp !== null) {
        bufferController.started = true;
      }
      syncStartupGates();
      await bufferController.beforeRead(signal);
      await processPart(pendingParts.shift());
    }
  };
  const reader = response.body.getReader();
  try {
    while (true) {
      syncStartupGates();
      await bufferController.beforeRead(signal);
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(value);
      await drainParts();
    }
    await drainParts();
    parser.finish();
    inputEnded = true;
    bufferController.setStartDelaySeconds(videoOnly() ? 0 : playbackTimeline.videoStartDelaySeconds());
    await bufferController.finish(signal);
  } catch (error) {
    if (error?.name !== "AbortError") {
      dbg("multipart: error", { name: error?.name, message: error?.message, videoParts });
    }
    try {
      await reader.cancel(error);
    } catch (_cancelError) {
      // Preserve the playback error that caused cancellation.
    }
    throw error;
  } finally {
    reader.releaseLock?.();
  }
}

  return {
    PlaybackVideoMuxer,
    PlaybackVideoClock,
    playMultipartResponse,
    parseDgfFrameTime,
    createPlaybackMuxerOptions,
  };
}));
